import React from "react";
import { Modal } from "react-bootstrap";
import { Button, ButtonType } from "../Components/Button/Button";
import { MdCheck, MdClose, MdEdit, MdOutlineMotionPhotosOn, MdCalendarToday } from "react-icons/md";
import { TestPowerStatus } from "./MetaLiftCreate";
import "./PowerCheckModals.scss";
import { CellPowerResult, formatEventNames } from "./MetaLiftStudyUtils";
import { formatMoney, formatPercent } from "@blisspointmedia/bpm-types/dist/formatUtils";
import { CampaignCell, ObjectiveCell } from "@blisspointmedia/bpm-types/dist/MetaLiftStudy";

interface PowerCheckModalsProps {
  showRunningModal: boolean;
  showResultModal: boolean;
  showReviewModal: boolean;
  showSuccessModal: boolean;
  onCloseRunningModal: () => void;
  onCloseResultModal: () => void;
  onCloseReviewModal: () => void;
  onCloseSuccessModal: () => void;
  testPowerStatus: TestPowerStatus;
  neededDailySpend: number;
  historicalDailySpend: number;
  testLengthDays: number;
  cellPowerResults: CellPowerResult[];
  testName: string;
  testDescription: string;
  testHypothesis: string;
  plannedActions: string;
  selectedTestLevel: string;
  totalBudget: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  campaignCells: CampaignCell[];
  objectiveCells: ObjectiveCell[];
  onReviewAndCreate: () => void;
  onEditTest: () => void;
  onCreateTest: () => void;
  onOpenCreatedTests: () => void;
  createdTestId: string;
  isCreating?: boolean;
  isCellToCellSensitivityPowered: boolean;
}

export const RunningPowerCheckModal: React.FC<{
  show: boolean;
  onHide: () => void;
}> = ({ show, onHide }) => (
  <Modal show={show} onHide={onHide} className="powerCheckModal powerCheckModalRunning" centered>
    <Modal.Body className="powerCheckModalBody powerCheckModalBodyRunning">
      <div className="powerCheckRunningContainer">
        <span className="powerCheckRunningText">Running Power Check</span>
        <MdOutlineMotionPhotosOn size={38.333} className="powerCheckRunningIcon" />
      </div>
    </Modal.Body>
  </Modal>
);

const ModalFailingCellCard: React.FC<{
  cellResult: CellPowerResult;
  subtitle: string;
}> = ({ cellResult, subtitle }) => (
  <div className="powerCheckCellCard">
    <div className="powerCheckCellCardHeader">
      <div className="powerCheckCellCardIcon">
        <MdClose size={16} />
      </div>
      <div className="powerCheckCellCardInfo">
        <div className="powerCheckCellCardName">{cellResult.cellName}</div>
        {subtitle && <div className="powerCheckCellCardSubtitle">{subtitle}</div>}
      </div>
    </div>
    <div className="powerCheckCellCardBudget">
      <div className="powerCheckCellCardBudgetRow">
        <span className="powerCheckCellCardBudgetLabel">Min required Budget:</span>
        <span className="powerCheckCellCardBudgetValue">
          {formatMoney(cellResult.minRequiredBudget)}
        </span>
      </div>
      <div className="powerCheckCellCardBudgetRow">
        <span className="powerCheckCellCardBudgetLabel powerCheckCellCardBudgetLabelError">
          Configuration Budget:
        </span>
        <span className="powerCheckCellCardBudgetValue powerCheckCellCardBudgetValueError">
          {formatMoney(cellResult.configurationBudget)}
        </span>
      </div>
    </div>
  </div>
);

const getCellSubtitleForModal = (
  cellResult: CellPowerResult,
  campaignCells: CampaignCell[]
): string => {
  const matchingCell = campaignCells.find(c => c.id === cellResult.cellId);
  if (!matchingCell || matchingCell.selectedItems.length === 0) {
    return "";
  }
  return matchingCell.selectedItems.map(item => item.id).join(", ");
};

export const PowerCheckResultModal: React.FC<{
  show: boolean;
  onHide: () => void;
  cellPowerResults: CellPowerResult[];
  neededDailySpend: number;
  historicalDailySpend: number;
  testLengthDays: number;
  campaignCells: CampaignCell[];
  onReviewAndCreate: () => void;
  onEditTest: () => void;
  isCellToCellSensitivityPowered: boolean;
}> = ({
  show,
  onHide,
  cellPowerResults,
  neededDailySpend,
  historicalDailySpend,
  testLengthDays,
  campaignCells,
  onReviewAndCreate,
  onEditTest,
  isCellToCellSensitivityPowered,
}) => {
  const isWellPowered =
    cellPowerResults.length > 0 &&
    cellPowerResults.every(c => c.isPowered) &&
    isCellToCellSensitivityPowered;
  const failingCells = cellPowerResults.filter(c => !c.isPowered);

  return (
    <Modal show={show} onHide={onHide} className="powerCheckModal powerCheckModalResult" centered>
      <Modal.Body className="powerCheckModalBody powerCheckModalBodyResult">
        <div className="powerCheckResultTopRow">
          <div
            className={`powerCheckResultIcon ${
              isWellPowered ? "powerCheckResultIconSuccess" : "powerCheckResultIconError"
            }`}
          >
            {isWellPowered ? <MdCheck size={57.54} /> : <MdClose size={57.54} />}
          </div>
          <div className="powerCheckResultTitle">
            {isWellPowered ? "Test is Well Powered!" : "Test is Not Powered"}
          </div>
        </div>

        {isWellPowered && (
          <div className="powerCheckResultDetails">
            <div className="powerCheckResultRow">
              <span className="powerCheckResultLabel">Minimum Required Budget:</span>
              <span className="powerCheckResultValue">
                {formatMoney(neededDailySpend * testLengthDays)}
              </span>
            </div>
            <div className="powerCheckResultRow">
              <span className="powerCheckResultLabel powerCheckResultLabelSuccess">
                Configuration Budget:
              </span>
              <span className="powerCheckResultValue powerCheckResultValueSuccess">
                {formatMoney(historicalDailySpend * testLengthDays)}
              </span>
            </div>
          </div>
        )}

        {!isWellPowered && failingCells.length > 0 && (
          <div className="powerCheckFailingCells">
            {failingCells.map(cellResult => (
              <ModalFailingCellCard
                key={cellResult.cellId}
                cellResult={cellResult}
                subtitle={getCellSubtitleForModal(cellResult, campaignCells)}
              />
            ))}
          </div>
        )}

        <div className="powerCheckDurationBox">
          <MdCalendarToday size={20} className="powerCheckDurationIcon" />
          <span className="powerCheckDurationText">
            Based on the current test setup, the minimum required duration is {testLengthDays} days.
          </span>
        </div>
      </Modal.Body>
      <Modal.Footer className="powerCheckModalFooter">
        <Button type={ButtonType.OUTLINED} onClick={onHide} size="sm">
          Cancel
        </Button>
        {isWellPowered ? (
          <Button type={ButtonType.FILLED} onClick={onReviewAndCreate} size="sm">
            Review and Create Test
          </Button>
        ) : (
          <Button type={ButtonType.FILLED} onClick={onEditTest} size="sm">
            Edit Test
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const ReviewTestModal: React.FC<{
  show: boolean;
  onHide: () => void;
  testName: string;
  testDescription: string;
  testHypothesis: string;
  plannedActions: string;
  selectedTestLevel: string;
  totalBudget: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  campaignCells: CampaignCell[];
  objectiveCells: ObjectiveCell[];
  onEdit: () => void;
  onCreate: () => void;
  isCreating?: boolean;
  isCellToCellSensitivityPowered: boolean;
  cellPowerResults: CellPowerResult[];
}> = ({
  show,
  onHide,
  testName,
  testDescription,
  testHypothesis,
  plannedActions,
  selectedTestLevel,
  totalBudget,
  startDate,
  startTime,
  endDate,
  endTime,
  campaignCells,
  objectiveCells,
  onEdit,
  onCreate,
  isCreating = false,
  isCellToCellSensitivityPowered,
  cellPowerResults,
}) => {
  const isWellPowered =
    cellPowerResults.length > 0 &&
    cellPowerResults.every(c => c.isPowered) &&
    isCellToCellSensitivityPowered;
  const formatDateTime = (date: string, time: string): string => {
    if (!date) {
      return "[Date / Time Selected]";
    }
    return time ? `${date} ${time}` : date;
  };

  return (
    <Modal show={show} onHide={onHide} className="powerCheckModal powerCheckModalReview" centered>
      <Modal.Header className="reviewModalHeader">
        <div className="reviewModalTitle">Review {testName || "Test name"}</div>
        <button className="reviewModalCloseButton" onClick={onHide}>
          <MdClose size={24} />
        </button>
      </Modal.Header>
      <Modal.Body className="reviewModalBody">
        <div className="reviewSection">
          <div className="reviewSectionHeader">Summary</div>
          <div className="reviewRow">
            <span className="reviewLabel">Test Hypothesis</span>
            <span className="reviewValue">{testHypothesis || "[Text that the user input]"}</span>
          </div>
          <div className="reviewRow">
            <span className="reviewLabel">Test Description</span>
            <span className="reviewValue">{testDescription || "[Text that the user input]"}</span>
          </div>
          <div className="reviewRow">
            <span className="reviewLabel">Planned Actions</span>
            <span className="reviewValue">{plannedActions || "[Text that the user input]"}</span>
          </div>
          <div className="reviewRow">
            <span className="reviewLabel">Test Level</span>
            <span className="reviewValue">{selectedTestLevel || "Campaigns"}</span>
          </div>
          <div className="reviewRow">
            <span className="reviewLabel">Total Budget</span>
            <span className="reviewValue">{totalBudget || "{total budget}"}</span>
          </div>
          <div className="reviewRow">
            <span className="reviewLabel">Test Power</span>
            <span
              className={`reviewValue ${isWellPowered ? "reviewValueSuccess" : "reviewValueError"}`}
            >
              {isWellPowered ? "Well Powered" : "Not Well Powered"}
              {isWellPowered ? <MdCheck size={16} /> : <MdClose size={16} />}
            </span>
          </div>
        </div>

        <div className="reviewSection">
          <div className="reviewSectionHeader">Schedule</div>
          <div className="reviewRow">
            <span className="reviewLabel">Start</span>
            <span className="reviewValue">{formatDateTime(startDate, startTime)}</span>
          </div>
          <div className="reviewRow">
            <span className="reviewLabel">End</span>
            <span className="reviewValue">{formatDateTime(endDate, endTime)}</span>
          </div>
        </div>

        <div className="reviewSection">
          <div className="reviewSectionHeader">Cells</div>
          {campaignCells.map((cell, index) => (
            <div key={cell.id} className="reviewCellGroup">
              <div className="reviewCellNumber">{index + 1}</div>
              <div className="reviewCellDetails">
                <div className="reviewCellRow">
                  <span className="reviewCellLabel">Cell Name</span>
                  <span className="reviewCellValue">{cell.name || "Unnamed Cell"}</span>
                </div>
                <div className="reviewCellRow">
                  <span className="reviewCellLabel">Test %</span>
                  <span className="reviewCellValue">{formatPercent(cell.testPercentage, 0)}</span>
                </div>
                <div className="reviewCellRow">
                  <span className="reviewCellLabel">Holdout %</span>
                  <span className="reviewCellValue">
                    {formatPercent(cell.controlPercentage, 0)}
                  </span>
                </div>
                <div className="reviewCellRow">
                  <span className="reviewCellLabel">
                    {" "}
                    {cell.selectedItems && cell.selectedItems.length > 1
                      ? `${selectedTestLevel}s`
                      : `${selectedTestLevel}`}
                  </span>
                  <span
                    className="reviewCellValue"
                    title={cell.selectedItems.map(item => item.name).join(", ")}
                  >
                    {cell.selectedItems.map(item => item.name).join(", ")}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="reviewSection">
          <div className="reviewSectionHeader">Objectives and Conversion Sources</div>
          {objectiveCells.map((cell, index) => (
            <div key={cell.id} className="reviewObjectiveRow">
              <span className="reviewObjectiveNumber">{index + 1}</span>
              <span className="reviewObjectiveLabel">{cell.name || `Objective ${index + 1}`}</span>
              <span className="reviewObjectiveValue">
                Source: {formatEventNames(cell.eventNames || [])}
              </span>
            </div>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer className="reviewModalFooter">
        <div className="reviewFooterLeft"></div>
        <div className="reviewFooterRight">
          <Button type={ButtonType.OUTLINED} onClick={onEdit} size="sm" disabled={isCreating}>
            <MdEdit size={16} /> Edit
          </Button>
          <Button type={ButtonType.FILLED} onClick={onCreate} size="sm" disabled={isCreating}>
            {isCreating ? "Creating..." : "Create Test"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const TestCreatedModal: React.FC<{
  show: boolean;
  onHide: () => void;
  testId: string;
  onOpenCreatedTests: () => void;
}> = ({ show, onHide, testId, onOpenCreatedTests }) => (
  <Modal show={show} onHide={onHide} className="powerCheckModal powerCheckModalSuccess" centered>
    <Modal.Body className="powerCheckModalBody powerCheckModalBodySuccess">
      <div className="testCreatedIcon">
        <MdCheck size={52.77} />
      </div>
      <div className="testCreatedHolder">
        <div className="testCreatedTitle">
          Test created! This test will run in Meta on its start date!
        </div>
        <div className="testCreatedId">Test ID: {testId || "2342453451"}</div>
        <div className="testCreatedInfo">
          If you need to adjust your test details, go to the Experiments page within Ads Manager.
          You can make edits to your test until its live date.
        </div>
      </div>
    </Modal.Body>
    <Modal.Footer className="powerCheckModalFooter powerCheckModalFooterSuccess">
      <Button type={ButtonType.FILLED} onClick={onOpenCreatedTests} size="sm">
        Open Created Tests
      </Button>
    </Modal.Footer>
  </Modal>
);

const PowerCheckModals: React.FC<PowerCheckModalsProps> = ({
  showRunningModal,
  showResultModal,
  showReviewModal,
  showSuccessModal,
  onCloseRunningModal,
  onCloseResultModal,
  onCloseReviewModal,
  onCloseSuccessModal,
  testPowerStatus,
  neededDailySpend,
  historicalDailySpend,
  testLengthDays,
  cellPowerResults,
  testName,
  testDescription,
  testHypothesis,
  plannedActions,
  selectedTestLevel,
  totalBudget,
  startDate,
  startTime,
  endDate,
  endTime,
  campaignCells,
  objectiveCells,
  onReviewAndCreate,
  onEditTest,
  onCreateTest,
  onOpenCreatedTests,
  createdTestId,
  isCreating = false,
  isCellToCellSensitivityPowered,
}) => {
  return (
    <>
      <RunningPowerCheckModal show={showRunningModal} onHide={onCloseRunningModal} />

      <PowerCheckResultModal
        show={showResultModal}
        onHide={onCloseResultModal}
        cellPowerResults={cellPowerResults}
        neededDailySpend={neededDailySpend}
        historicalDailySpend={historicalDailySpend}
        testLengthDays={testLengthDays}
        campaignCells={campaignCells}
        onReviewAndCreate={onReviewAndCreate}
        onEditTest={onEditTest}
        isCellToCellSensitivityPowered={isCellToCellSensitivityPowered}
      />

      <ReviewTestModal
        show={showReviewModal}
        onHide={onCloseReviewModal}
        testName={testName}
        testDescription={testDescription}
        testHypothesis={testHypothesis}
        plannedActions={plannedActions}
        selectedTestLevel={selectedTestLevel}
        totalBudget={totalBudget}
        startDate={startDate}
        startTime={startTime}
        endDate={endDate}
        endTime={endTime}
        campaignCells={campaignCells}
        objectiveCells={objectiveCells}
        onEdit={onEditTest}
        onCreate={onCreateTest}
        isCreating={isCreating}
        cellPowerResults={cellPowerResults}
        isCellToCellSensitivityPowered={isCellToCellSensitivityPowered}
      />

      <TestCreatedModal
        show={showSuccessModal}
        onHide={onCloseSuccessModal}
        testId={createdTestId}
        onOpenCreatedTests={onOpenCreatedTests}
      />
    </>
  );
};

export default PowerCheckModals;
